import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Phone, Search, RefreshCw, CalendarX2, Trash2, FileSpreadsheet, MapPin, Navigation, Palmtree, QrCode, Download } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUS_LABELS = {
  en_attente_paiement: "En attente de paiement",
  confirmee: "Confirmée",
  en_route: "En route",
  livree: "Livrée",
  annulee: "Annulée",
};

const STATUS_STYLES = {
  en_attente_paiement: "bg-amber-100 text-amber-800",
  confirmee: "bg-blue-100 text-blue-800",
  en_route: "bg-violet-100 text-violet-800",
  livree: "bg-green-100 text-green-800",
  annulee: "bg-red-100 text-red-700",
};

const PAYMENT_BADGES = {
  paid: { label: "Payé CB", cls: "bg-green-100 text-green-800" },
  pending: { label: "CB non payé", cls: "bg-amber-100 text-amber-800" },
  a_la_livraison: { label: "Espèces", cls: "bg-orange-100 text-orange-800" },
  virement_attendu: { label: "Virement", cls: "bg-sky-100 text-sky-800" },
};

const TIME_SLOTS = ["Matin (8h – 12h)", "Après-midi (12h – 17h)", "Après-midi (12h – 16h)"];
const EDITABLE = ["confirmee", "en_route", "livree", "annulee"];

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [blockDate, setBlockDate] = useState("");
  const [blockSlot, setBlockSlot] = useState("journee");
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);
  const [routeDate, setRouteDate] = useState(new Date().toISOString().split("T")[0]);
  const [routeBookings, setRouteBookings] = useState([]);
  const [absence, setAbsence] = useState({ active: false, start: "", end: "" });
  const [absenceSaved, setAbsenceSaved] = useState(false);

  const handleAuthError = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        localStorage.removeItem("pc_token");
        navigate("/admin");
        return true;
      }
      return false;
    },
    [navigate]
  );

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (query) params.q = query;
      const [b, s, bl, ab] = await Promise.all([
        api.get("/admin/bookings", { params }),
        api.get("/admin/stats"),
        api.get("/admin/blocked-slots"),
        api.get("/settings/absence"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setBlocked(bl.data);
      setAbsence({ active: ab.data.active, start: ab.data.start || "", end: ab.data.end || "" });
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query, handleAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/admin/bookings", { params: { date: routeDate } })
      .then(({ data }) => {
        const slotOrder = (s) => (s.startsWith("Matin") ? 0 : 1);
        setRouteBookings(
          data
            .filter((b) => b.status === "confirmee" || b.status === "en_route")
            .sort((a, b) => slotOrder(a.time_slot) - slotOrder(b.time_slot) || a.client_name.localeCompare(b.client_name))
        );
      })
      .catch((err) => handleAuthError(err));
  }, [routeDate, bookings, handleAuthError]);

  const updateStatus = async (booking, status) => {
    setBookings((list) => list.map((x) => (x.id === booking.id ? { ...x, status } : x)));
    try {
      await api.patch(`/admin/bookings/${booking.id}`, { status });
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (err) {
      if (!handleAuthError(err)) load();
    }
  };

  const addBlocked = async (e) => {
    e.preventDefault();
    if (!blockDate) return;
    try {
      await api.post("/admin/blocked-slots", { date: blockDate, time_slot: blockSlot });
      const bl = await api.get("/admin/blocked-slots");
      setBlocked(bl.data);
      setBlockDate("");
    } catch (err) {
      handleAuthError(err);
    }
  };

  const removeBlocked = async (id) => {
    setBlocked((list) => list.filter((x) => x.id !== id));
    try {
      await api.delete(`/admin/blocked-slots/${id}`);
    } catch (err) {
      if (!handleAuthError(err)) load();
    }
  };

  const logout = () => {
    localStorage.removeItem("pc_token");
    navigate("/admin");
  };

  const saveAbsence = async () => {
    setAbsenceSaved(false);
    try {
      await api.put("/admin/settings/absence", {
        active: absence.active,
        start: absence.start || null,
        end: absence.end || null,
      });
      setAbsenceSaved(true);
      setTimeout(() => setAbsenceSaved(false), 3000);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const exportCsv = async () => {    setExporting(true);
    try {
      const res = await api.get("/admin/export.csv", { params: { month: exportMonth }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `livraisons-${exportMonth}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setExporting(false);
    }
  };

  const statCards = stats
    ? [
        { label: "À traiter", value: stats.confirmee },
        { label: "En route", value: stats.en_route },
        { label: "Livrées", value: stats.livree },
        { label: "Encaissé CB", value: eur(stats.revenue) },
      ]
    : [];

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#F2F8FB]" data-testid="admin-dashboard">
      <header className="border-b border-ink/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="flex min-h-[44px] items-center gap-2 rounded-full border border-ink/15 px-4 text-sm font-semibold text-ink/70 transition-colors hover:text-forest"
            >
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex min-h-[44px] items-center gap-2 rounded-full bg-ink px-4 text-sm font-semibold text-white transition-colors hover:bg-forest"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="mb-6 font-serif text-2xl font-semibold text-ink sm:text-3xl">Mes livraisons</h1>

        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5" data-testid={`stat-${c.label}`}>
              <p className="text-sm font-medium text-ink/50">{c.label}</p>
              <p className="mt-1 font-serif text-3xl font-semibold text-forest">{c.value}</p>
            </div>
          ))}
        </div>

        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5 sm:p-6" data-testid="blocked-slots-section">
          <div className="mb-4 flex items-center gap-3">
            <CalendarX2 className="h-5 w-5 text-terra" />
            <h2 className="font-serif text-lg font-semibold text-ink sm:text-xl">Indisponibilités</h2>
          </div>
          <form onSubmit={addBlocked} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-ink/50">Date</span>
              <input
                type="date"
                required
                min={today}
                value={blockDate}
                onChange={(e) => setBlockDate(e.target.value)}
                data-testid="block-date-input"
                className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-ink/50">Créneau</span>
              <select
                value={blockSlot}
                onChange={(e) => setBlockSlot(e.target.value)}
                data-testid="block-slot-select"
                className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
              >
                <option value="journee">Journée entière</option>
                {TIME_SLOTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              data-testid="block-slot-button"
              className="min-h-[44px] rounded-full bg-terra px-5 text-sm font-semibold text-white transition-colors hover:bg-terra-dark"
            >
              Bloquer ce créneau
            </button>
          </form>
          {blocked.length > 0 ? (
            <ul className="mt-4 flex flex-wrap gap-2" data-testid="blocked-slots-list">
              {blocked.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center gap-2 rounded-full bg-red-50 px-4 py-2 text-sm font-medium text-red-800 ring-1 ring-red-200"
                >
                  {b.date} — {b.time_slot === "journee" ? "Journée entière" : b.time_slot}
                  <button
                    onClick={() => removeBlocked(b.id)}
                    aria-label={`Libérer le créneau du ${b.date}`}
                    data-testid={`unblock-slot-${b.id}`}
                    className="rounded-full p-1 transition-colors hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-ink/45">Aucune indisponibilité : tous les créneaux sont ouverts à la réservation.</p>
          )}
        </section>

        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5 sm:p-6" data-testid="absence-section">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <Palmtree className="h-5 w-5 text-amber-600" />
              <div>
                <h2 className="font-serif text-lg font-semibold text-ink sm:text-xl">Message d'absence</h2>
                <p className="text-sm text-ink/50">Un bandeau « En congés » s'affiche en haut du site pour vos visiteurs.</p>
              </div>
            </div>
            <button
              onClick={() => setAbsence((a) => ({ ...a, active: !a.active }))}
              role="switch"
              aria-checked={absence.active}
              data-testid="absence-toggle"
              className={`relative h-8 w-14 shrink-0 rounded-full transition-colors duration-200 ${absence.active ? "bg-amber-500" : "bg-ink/15"}`}
            >
              <span
                className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all duration-200 ${absence.active ? "left-7" : "left-1"}`}
              />
            </button>
          </div>
          {absence.active && (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink/50">Du</span>
                <input
                  type="date"
                  value={absence.start}
                  onChange={(e) => setAbsence((a) => ({ ...a, start: e.target.value }))}
                  data-testid="absence-start-input"
                  className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink/50">Au</span>
                <input
                  type="date"
                  value={absence.end}
                  onChange={(e) => setAbsence((a) => ({ ...a, end: e.target.value }))}
                  data-testid="absence-end-input"
                  className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
                />
              </label>
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={saveAbsence}
              data-testid="absence-save-button"
              className="min-h-[44px] rounded-full bg-forest px-5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark"
            >
              Enregistrer
            </button>
            {absenceSaved && (
              <span className="text-sm font-semibold text-green-700" data-testid="absence-saved">
                Enregistré — visible sur le site.
              </span>
            )}
          </div>
        </section>

        <section className="mb-8 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5 sm:p-6" data-testid="day-route-section">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <Navigation className="h-5 w-5 text-terra" />
              <h2 className="font-serif text-lg font-semibold text-ink sm:text-xl">Feuille de route</h2>
            </div>
            <input
              type="date"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
              data-testid="route-date-input"
              className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
            />
          </div>
          {routeBookings.length === 0 ? (
            <p className="text-sm text-ink/45" data-testid="route-empty">Aucune livraison confirmée ce jour-là. Route libre !</p>
          ) : (
            <ol className="space-y-3">
              {routeBookings.map((b, i) => (
                <li
                  key={b.id}
                  className="flex flex-col gap-3 rounded-2xl border border-ink/10 p-4 sm:flex-row sm:items-center"
                  data-testid={`route-stop-${b.id}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-serif text-base font-semibold text-cream">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${b.time_slot.startsWith("Matin") ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>
                        {b.time_slot}
                      </span>
                      <span className="font-semibold text-ink">{b.client_name}</span>
                      <a href={`tel:${b.phone}`} className="flex items-center gap-1 text-sm text-forest hover:underline">
                        <Phone className="h-3 w-3" /> {b.phone}
                      </a>
                    </div>
                    <p className="mt-1.5 flex items-start gap-1.5 text-sm text-ink/65">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-terra" />
                      <span>
                        <strong className="font-medium text-ink/80">{b.pickup_address}</strong>
                        {" → "}
                        {b.delivery_address}
                        {b.description && <span className="block text-xs text-ink/45">{b.description}</span>}
                      </span>
                    </p>
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(b.pickup_address)}&destination=${encodeURIComponent(b.delivery_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`route-maps-${b.id}`}
                    className="flex min-h-[40px] shrink-0 items-center justify-center gap-2 rounded-full border border-forest/25 px-4 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream"
                  >
                    <Navigation className="h-4 w-4" /> Itinéraire
                  </a>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="mb-8 flex flex-col items-start gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5 sm:flex-row sm:items-center sm:p-6" data-testid="qrcode-section">
          <img
            src="/qrcode-angelivraisons.png"
            alt="QR code vers angelivraisons.com"
            className="h-28 w-28 rounded-2xl ring-1 ring-ink/10"
            data-testid="qrcode-image"
          />
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <QrCode className="h-5 w-5 text-forest" />
              <h2 className="font-serif text-lg font-semibold text-ink sm:text-xl">Votre QR code</h2>
            </div>
            <p className="mt-1 text-sm text-ink/50">
              Il ouvre angelivraisons.com. Imprimez-le sur vos flyers, cartes de visite ou affiches : vos clients
              scannent et réservent directement.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href="/qrcode-angelivraisons.png"
              download="qrcode-angelivraisons.png"
              data-testid="qrcode-download"
              className="flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-forest px-5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark"
            >
              <Download className="h-4 w-4" /> Télécharger
            </a>
            <a
              href="/flyer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="flyer-link"
              className="flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-forest/25 px-5 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream"
            >
              Voir le flyer A5
            </a>
          </div>
        </section>

        <section className="mb-8 flex flex-col items-start justify-between gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5 sm:flex-row sm:items-center sm:p-6" data-testid="export-section">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-forest" />
            <div>
              <h2 className="font-serif text-lg font-semibold text-ink sm:text-xl">Export comptable</h2>
              <p className="text-sm text-ink/50">Toutes les livraisons du mois, prêtes pour Excel ou votre expert-comptable.</p>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-ink/50">Mois</span>
              <input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                data-testid="export-month-input"
                className="min-h-[44px] rounded-xl border border-ink/10 bg-white px-4 text-sm outline-none focus:border-forest"
              />
            </label>
            <button
              onClick={exportCsv}
              disabled={exporting || !exportMonth}
              data-testid="export-csv-button"
              className="min-h-[44px] rounded-full bg-forest px-5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark disabled:opacity-60"
            >
              {exporting ? "Préparation…" : "Télécharger le CSV"}
            </button>
          </div>
        </section>

        <div className="mb-5 flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/35" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un nom, un téléphone, une référence…"
              data-testid="admin-search-input"
              className="min-h-[48px] w-full rounded-xl border border-ink/10 bg-white pl-11 pr-4 text-base outline-none focus:border-forest"
            />
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-status-filter"
            className="min-h-[48px] rounded-xl border border-ink/10 bg-white px-4 text-base outline-none focus:border-forest"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-ink/5">
          {loading ? (
            <p className="p-10 text-center text-ink/50">Chargement…</p>
          ) : bookings.length === 0 ? (
            <p className="p-10 text-center text-ink/50" data-testid="admin-empty-state">
              Aucune réservation pour le moment.
            </p>
          ) : (
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-ink/10 text-xs uppercase tracking-wider text-ink/45">
                  <th className="px-5 py-4 font-semibold">Réf.</th>
                  <th className="px-5 py-4 font-semibold">Client</th>
                  <th className="px-5 py-4 font-semibold">Type</th>
                  <th className="px-5 py-4 font-semibold">Date & créneau</th>
                  <th className="px-5 py-4 font-semibold">Trajet</th>
                  <th className="px-5 py-4 font-semibold">Montant</th>
                  <th className="px-5 py-4 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/5">
                {bookings.map((b) => {
                  const badge = PAYMENT_BADGES[b.payment_status] || PAYMENT_BADGES.pending;
                  return (
                    <tr key={b.id} className="align-top transition-colors hover:bg-cream/60" data-testid={`admin-booking-row-${b.id}`}>
                      <td className="px-5 py-4 font-mono text-xs font-semibold text-ink/60">{b.ref}</td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-ink">{b.client_name}</p>
                        <a href={`tel:${b.phone}`} className="mt-0.5 flex items-center gap-1 text-forest hover:underline">
                          <Phone className="h-3 w-3" /> {b.phone}
                        </a>
                        {b.description && <p className="mt-1 max-w-[220px] text-xs text-ink/50">{b.description}</p>}
                      </td>
                      <td className="px-5 py-4 text-ink/70">{b.delivery_label}</td>
                      <td className="px-5 py-4 text-ink/70">
                        {b.date}
                        <br />
                        <span className="text-xs text-ink/50">{b.time_slot}</span>
                      </td>
                      <td className="max-w-[220px] px-5 py-4 text-xs leading-relaxed text-ink/60">
                        <p>
                          <span className="font-semibold text-ink/70">De :</span> {b.pickup_address}
                        </p>
                        <p className="mt-1">
                          <span className="font-semibold text-ink/70">À :</span> {b.delivery_address}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-ink">{eur(b.total)}</p>
                        <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {b.status === "en_attente_paiement" ? (
                          <span className={`inline-block rounded-full px-3 py-1.5 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
                            {STATUS_LABELS[b.status]}
                          </span>
                        ) : (
                          <select
                            value={b.status}
                            onChange={(e) => updateStatus(b, e.target.value)}
                            data-testid={`admin-status-select-${b.id}`}
                            className={`min-h-[40px] rounded-full border-0 px-3 text-xs font-semibold outline-none ring-1 ring-ink/10 ${STATUS_STYLES[b.status]}`}
                          >
                            {EDITABLE.map((s) => (
                              <option key={s} value={s}>
                                {STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
